import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentEmployee, selectIsTipCalcFormDirty, setCurrentEmployeeRequest } from 'store/App';
import { selectCurrentEmployeeManagedSites } from 'store/Employers';
import { LoadingSkeleton } from '../../screens/SiteSettings/styles';
import { IEmployerSite } from '../../types';
import { AlertContent } from '../Alert';
import { ModalFactory } from '../Modal';
import ConnectedSiteSelector from './ConnectedSiteSelector';

const TipCalculatorSiteSelector = () => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const [siteToConfirm, setSiteToConfirm] = useState<IEmployerSite>();
  const [showSelect, setShowSelect] = useState(true);
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployeeSites = useSelector(selectCurrentEmployeeManagedSites);
  const isTipCalcFormDirty = useSelector(selectIsTipCalcFormDirty);

  const onConfirmSiteSelect = (site: IEmployerSite) => dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: site.id }));

  const handleSiteSelect = useCallback(
    (selected: IEmployerSite) => {
      if (selected) {
        const siteId = selected?.id;
        const site = (currentEmployeeSites || []).find((site) => site.id === siteId);

        if (isTipCalcFormDirty) {
          setShowSelect(false);
          setSiteToConfirm(site);
          showConfirmModal();
        } else {
          onConfirmSiteSelect(site);
        }
      }
    },
    [currentEmployeeSites, isTipCalcFormDirty]
  );

  const onClose = () => {
    setShowSelect(true);
    hideConfirmModal();
  };

  const [showConfirmModal, hideConfirmModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideConfirmModal} isActive closeOnOutSideClick={false}>
        <AlertContent
          title="Warning"
          message="Changing your site will delete all unsaved rules. Do you want to proceed?"
          leftActionLabel="Cancel"
          rightActionLabel="Proceed"
          onClose={onClose}
          onLeftAction={onClose}
          onRightAction={() => {
            onConfirmSiteSelect(siteToConfirm);
            onClose();
          }}
        />
      </ModalFactory>
    ),
    [siteToConfirm]
  );

  useEffect(() => {
    if (!isMounted.current && currentEmployeeSites) {
      isMounted.current = true;

      const siteId = currentEmployee.siteId || currentEmployeeSites.find((s) => s.settings.tipCalculator)?.id || currentEmployeeSites[0]?.id;
      if (siteId) dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId }));
    }
  }, [dispatch, currentEmployeeSites]);

  return showSelect ? <ConnectedSiteSelector onSiteSelect={handleSiteSelect} /> : <LoadingSkeleton />;
};

export default TipCalculatorSiteSelector;
